import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, Signal } from '@angular/core';
import { InputModule, RadioModule, SelectModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReferenceNumberDomesticFormGroup } from '../../../models/cod-create.model';
import { ReferenceNumberTypeDomestic } from '@cod-monorepo/interfaces';
import { NatureOfComplaintDomestic } from '@cod-monorepo/interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, debounceTime, distinctUntilChanged, filter, Observable, tap } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { AddErrorMessage, LoadOrderLineItems, RemoveErrorMessage } from '../../../state/cod.action';
import { CodState } from '../../../state/cod.state';
import { WorkflowError } from '../../../../shared/enums/workflow-error';

@Component({
    selector: 'app-cod-reference-number-domestic-step',
    templateUrl: './reference-number-domestic.component.html',
    imports: [CommonModule, SelectModule, InputModule, FormsModule, ReactiveFormsModule, RadioModule],
    standalone: true
})
export class CodReferenceNumberDomesticStepComponent implements OnInit {
    @Input() referenceNumberDomesticForm: FormGroup<ReferenceNumberDomesticFormGroup> | undefined;
    @Input() readonly = false;

    public readonly isQueryingReferenceNumber: Signal<boolean> = select(CodState.queryingReferenceNumber);
    public referenceNumberFound$: Observable<boolean | null> = inject(Store).select(CodState.referenceNumberFound);

    public refernceNumberTypes = ReferenceNumberTypeDomestic;
    public natureOfComplaints: string[] = [];

    constructor(private destroyRef: DestroyRef, private store: Store) {}

    ngOnInit(): void {
        this.initNatureOfComplaints();
        this.initReferenceNumberChanges();
        this.initProductDiscrepancyChanges();
        this.initReferenceNumberFound();
    }

    private initNatureOfComplaints(): void {
        if (this.readonly) {
            this.natureOfComplaints = Object.values(NatureOfComplaintDomestic);
        } else {
            // Note: Billing Adjustment was remove, keep for compatibility reasons
            this.natureOfComplaints = Object.values(NatureOfComplaintDomestic).filter(
                noc => noc !== NatureOfComplaintDomestic.BILLING_ADJUSTMENT
            );
        }
    }

    private initReferenceNumberChanges(): void {
        if (this.referenceNumberDomesticForm?.disabled) {
            return;
        }

        const referenceNumberControl = this.referenceNumberDomesticForm?.controls.referenceNumber;
        const referenceNumberTypeControl = this.referenceNumberDomesticForm?.controls.referenceNumberType;

        if (!referenceNumberControl || !referenceNumberTypeControl) {
            return;
        }

        combineLatest([
            referenceNumberControl.valueChanges.pipe(
                distinctUntilChanged(),
                filter(value => !!value)
            ),
            referenceNumberTypeControl.valueChanges.pipe(
                distinctUntilChanged(),
                filter(value => !!value)
            )
        ])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                debounceTime(300),
                filter(([referenceNumber, referenceNumberType]) => !!referenceNumber && !!referenceNumberType),
                tap(([referenceNumber, referenceNumberType]) => {
                    this.loadReferencenNumberData(referenceNumber as string, referenceNumberType);
                })
            )
            .subscribe();
    }

    private loadReferencenNumberData(referenceNumber: string, type: ReferenceNumberTypeDomestic | undefined): void {
        this.store.dispatch(new LoadOrderLineItems(referenceNumber, type));
    }

    private initReferenceNumberFound(): void {
        this.referenceNumberFound$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .pipe(
                tap(found => {
                    this.referenceNumberDomesticForm?.controls.referenceNumberValid.setValue(found);

                    if (found) {
                        this.store.dispatch(new RemoveErrorMessage(WorkflowError.SOURCE_PLANT_OUT_OF_SCOPE));
                    }

                    if (found === false && !this.isQueryingReferenceNumber()) {
                        this.store.dispatch(new AddErrorMessage(WorkflowError.SOURCE_PLANT_OUT_OF_SCOPE));
                    }
                })
            )
            .subscribe();
    }

    private initProductDiscrepancyChanges(): void {
        this.referenceNumberDomesticForm?.controls.productDiscrepancy.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(value => {
                    if (value) {
                        this.store.dispatch(new AddErrorMessage(WorkflowError.SUBMITTED_PRODUCT_DISCREPANCY));
                    } else {
                        this.store.dispatch(new RemoveErrorMessage(WorkflowError.SUBMITTED_PRODUCT_DISCREPANCY));
                    }
                })
            )
            .subscribe();
    }
}
